import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useDataProvider } from "ra-core";

import { resolveEffectiveDealStages } from "../deals/stageConfig";
import type { DealStageConfig } from "../types";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultDealPipelineStatuses,
  defaultDealStages,
} from "./defaultConfiguration";
import {
  useConfigurationUpdater,
  useDealStagesUpdater,
  type ConfigurationContextValue,
} from "./ConfigurationContext";

export const useConfigurationLoader = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const updateConfiguration = useConfigurationUpdater();
  const updateDealStages = useDealStagesUpdater();

  const { data } = useQuery<ConfigurationContextValue>({
    queryKey: ["configuration"],
    queryFn: () => dataProvider.getConfiguration(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      // A `configuration` row saved before Almikia Epic E2 may still carry
      // the legacy Atomic `dealStages` / `dealPipelineStatuses`. The Almikia
      // states always win (also enforced in useConfigurationContext), so
      // overwrite these two keys here rather than store stale values.
      updateConfiguration({
        ...data,
        dealStages: defaultDealStages,
        dealPipelineStatuses: defaultDealPipelineStatuses,
      });
    }
  }, [data, updateConfiguration]);

  // Almikia Epic EP (TASK-104): the ONE integration point that resolves
  // `deal_stage_configs` into the app's effective `dealStages` (DATA CONTRACT
  // EP (8)) -- every existing consumer of `useConfigurationContext().dealStages`
  // picks up configured labels/order/placement with no per-consumer edit.
  // Same 5-minute staleTime as the `configuration` query, so a direccion
  // change reaches another signed-in session at the next refresh or reload.
  const { data: stageConfigs } = useQuery<DealStageConfig[]>({
    queryKey: ["deal_stage_configs", "effective"],
    queryFn: async () => {
      try {
        const { data: configs } = await dataProvider.getList<DealStageConfig>(
          "deal_stage_configs",
          {
            filter: {},
            pagination: { page: 1, perPage: 1000 },
            sort: { field: "position", order: "ASC" },
          },
        );
        return configs;
      } catch {
        // DATA CONTRACT EP (4): absent configuration = E2 behavior. A data
        // provider that does not (yet) expose `deal_stage_configs` (e.g. a
        // minimal test fixture, or a genuine fetch failure) resolves to no
        // rows, exactly like an empty result set -- `resolveEffectiveDealStages`
        // then falls back to the E2 defaults on its own.
        return [];
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  useEffect(() => {
    // While loading or on error, `stageConfigs` stays undefined and the
    // dealStages store keeps its default value (the E2 list) -- the fallback
    // required by DATA CONTRACT EP (4).
    if (stageConfigs) {
      updateDealStages(resolveEffectiveDealStages(stageConfigs));
    }
  }, [stageConfigs, updateDealStages]);
};
